import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { ActionMenu, Icon, Text } from '../../../../components';
import type { FtuxTargetId } from '../../../../features/ftux/targets';
import { useMediaQuery } from '@/nova/hooks/useMediaQuery';
import { FIXED_HEADER, MOBILE } from '@/nova/theme/tokens';
import { gameMoveItems } from '../../game-move-menu';
import { isOnScreen, visibleBand, type ListPlacement, type ScreenInsets } from '../../game-list-geometry';
import { useGameListFilter } from '../../hooks/useGameListFilter';
import { useSortableGrid, type SortableGrid } from '../../hooks/useSortableGrid';
import { sendAway } from '../../send-away';
import { gameKey, gameThumbUrl, moveId, providerLabelOf, replaceHead, type SiteGame } from '../../site-games';
import { GameListFilter } from '../GameListFilter';
import { GameListRows } from '../GameListRows';
import { DragDemo, type DragGrip } from '../GameLists/dragDemo';
import { GameThumb } from '../GameThumb';
import {
  Wrap,
  Grid,
  Slot,
  Art,
  Img,
  Missing,
  MissingText,
  Name,
  OrderBadge,
  Meta,
  MetaText,
  TileMenu,
  DragLayer,
  DragGhost,
  Footer,
  MoreBtn,
  DragHandle,
} from './GameListGrid.styles';

export type GameListGridProps = {
  /** The list's games, in the current (draft) order. */
  games: SiteGame[];
  /** Provider id → display name, from the site's provider source. */
  providerNames: Record<string, string>;
  /** The full new order of game ids, after a drag or a move. */
  onReorder: (ids: string[]) => void;
  /** How many tiles to render before "Show more" (lists reach 1000+ games). */
  pageSize?: number;
  /** Where the list is shown: on the page (default) or inside a dialog's scroll area. */
  placement?: ListPlacement;
  /** Controls for the filter's row (see `GameListFilter`'s `actions`); with them that row stays pinned. */
  filterActions?: (compact: boolean) => React.ReactNode;
  /** Where the filter is drawn instead of over the list (the open list's heading, which stays put): the field alone, in
   *  that element, through a portal. Nothing then covers the top of the list. */
  filterPortal?: HTMLElement | null;
  /** With `filterActions`, on a phone: the field folds away to its magnifier until pressed (see GameListFilter). */
  collapsibleFilter?: boolean;
  /**
   * Whether a tile is being dragged right now. Desktop reorder is REALTIME — the order changes under the
   * pointer — so anything that reacts to the order (a page-level "unpublished changes" banner) would
   * appear mid-drag; this lets it wait for the drop.
   */
  onDraggingChange?: (dragging: boolean) => void;
  /** Games of the list the catalogue no longer holds: each is a placeholder tile in its place, saying so. */
  unavailable?: ReadonlySet<string>;
  /** Offers "Remove from list" on the unavailable games' tiles, which is how the owner takes them out. */
  onRemove?: (id: string) => void;
  /**
   * The games are the SLIM catalogue's records (one of the owner's own lists), which carry no art: a game with none of
   * its own has it read by id (`GameThumb`), batched with the other games drawn. Off (a site list, whose games carry
   * their art), a game is drawn with what it carries and nothing is read. On, the grid needs the app's redux store.
   */
  artById?: boolean;
  /**
   * Plays the FTUX's micro drag on the list again and again while true (see GameLists/dragDemo): the tiles' on
   * desktop, the rows' on phones. Only shown, never saved.
   */
  demoLoop?: boolean;
  /** This grid is the list of games the FTUX's games piece lights. By default the one on the page (a sheet's is not);
   *  the Games tab marks its open list's wherever it draws it (in its own column, on a wide desktop). */
  listTarget?: boolean;
};

const NOTHING_UNAVAILABLE: ReadonlySet<string> = new Set();

const DEFAULT_PAGE_SIZE = 60;

/** A tile's drag handle: a finger on it lifts the tile at once (the rest of the tile lifts by press and hold). */
const TILE_HANDLE = '[data-grip]';

/** The FTUX's games piece lights the page's list of games (not the one in a dialog). */
const GAMES_LIST_TARGET: FtuxTargetId = 'games-list';

/** The demo's hand on a tile: it holds the art (the slot's first, full-width square) by its middle and lifts it as it
 *  presses, raised as the real drag's tile (DragGhost: scale 1.04, the art's 14px corners); the owner's pointer on the
 *  grid takes over. */
const TILE_GRIP: DragGrip = {
  at: (_slot, box) => ({ x: box.width / 2, y: box.width / 2 }),
  holdS: 0,
  liftScale: 1.04,
  radius: 14,
  takeover: 'hover',
};

/** The art a game's tile shows, once its URL is known. */
const artImg = (src: string): React.ReactElement => <Img src={src} alt="" loading="lazy" draggable={false} />;

/** A game's art, as a tile shows it and as the tile in the hand does. A game the catalogue no longer holds has
 *  none: its tile says what happened to it instead. With `artById`, a game that carries no art of its own has it read
 *  by id (see `GameListGridProps.artById`). */
const GameArt = ({
  game,
  unavailable = false,
  artById = false,
}: {
  game: SiteGame;
  unavailable?: boolean;
  artById?: boolean;
}): React.ReactElement => {
  const thumb = unavailable ? undefined : gameThumbUrl(game);
  return (
    <Art>
      {thumb ? artImg(thumb) : artById && !unavailable ? <GameThumb game={game} render={artImg} /> : null}
      {unavailable ? (
        <Missing>
          <Icon name="eye-off" size={18} />
          <MissingText text="No longer in the catalogue. Players don't see it." />
        </Missing>
      ) : null}
    </Art>
  );
};

type GameTileProps = {
  game: SiteGame;
  index: number;
  total: number;
  providerNames: Record<string, string>;
  placement: ListPlacement;
  dragging: boolean;
  dimmed: boolean;
  current: boolean;
  registerSlot: SortableGrid['registerSlot'];
  slotDragProps: SortableGrid['slotDragProps'];
  band: () => ScreenInsets;
  onMove: (id: string, to: number) => void;
  onMenuOpen: () => void;
  unavailable: boolean;
  onRemove?: (id: string) => void;
  artById: boolean;
};

/**
 * One tile. Memoised, and every prop it takes is stable across a reorder except its own place, so a step of
 * a drag re-renders the tiles whose place changed rather than the whole list (with a move menu apiece).
 */
const GameTile = React.memo(function GameTile({
  game,
  index,
  total,
  providerNames,
  placement,
  dragging,
  dimmed,
  current,
  registerSlot,
  slotDragProps,
  band,
  onMove,
  onMenuOpen,
  unavailable,
  onRemove,
  artById,
}: GameTileProps): React.ReactElement {
  const { t } = useTranslation();
  const id = gameKey(game);
  const slotRef = React.useMemo(() => registerSlot(id), [registerSlot, id]);
  const dragProps = React.useMemo(() => slotDragProps(id), [slotDragProps, id]);
  const label = unavailable ? t('Unavailable game (ID {{id}})', { id }) : game.name || id;
  return (
    <Slot
      ref={slotRef}
      data-id={id}
      role="listitem"
      aria-label={label}
      draggable
      $draggable
      $dragging={dragging}
      $dimmed={dimmed}
      $current={current}
      $placement={placement}
      {...dragProps}
    >
      <OrderBadge>{index + 1}</OrderBadge>
      <GameArt game={game} unavailable={unavailable} artById={artById} />
      {unavailable ? <Name text="Unavailable game" $muted /> : <Name text={game.name || `#${id}`} raw />}
      <Meta>
        <DragHandle aria-hidden="true" data-grip="">
          <Icon name="drag-handle" size={16} />
        </DragHandle>
        {unavailable ? (
          <MetaText text="ID {{id}}" placeholders={{ id }} />
        ) : (
          <MetaText
            text="ID {{id}} · {{provider}}"
            placeholders={{ id, provider: providerLabelOf(game, providerNames, t) }}
          />
        )}
        <TileMenu>
          <ActionMenu
            align="end"
            label={t('Move {{name}}', { name: unavailable ? t('Unavailable game') : game.name || id })}
            viewportInsets={band}
            onOpenChange={(open) => {
              if (open) onMenuOpen();
            }}
            items={gameMoveItems(t, id, index, total, onMove, unavailable ? onRemove : undefined)}
          />
        </TileMenu>
      </Meta>
    </Slot>
  );
});

/**
 * A site game list as an editable, wrapping grid: square artwork like the site's own game rows, the game
 * name under the art and its ID + provider under that, in order left-to-right and top-to-bottom. Tiles are
 * dragged into a new order, or moved from their menu (to the top / bottom of the whole list, or one step),
 * which leaves the page where it is: the other games glide, and a game sent off screen drifts that way as
 * it goes. Phones get the same list as slim rows instead (`GameListRows`). Only the rendered window is
 * dragged; the games past it keep their positions until they are shown.
 *
 * A live filter sits above the list (`useGameListFilter`): the games that do not match fade back, every
 * keystroke scrolls to the first game that does, and previous / next move between the matches.
 */
export const GameListGrid = ({
  games,
  providerNames,
  onReorder,
  pageSize = DEFAULT_PAGE_SIZE,
  onDraggingChange,
  placement = 'page',
  filterActions,
  filterPortal = null,
  collapsibleFilter = false,
  unavailable = NOTHING_UNAVAILABLE,
  onRemove,
  artById = false,
  demoLoop = false,
  listTarget = placement === 'page',
}: GameListGridProps): React.ReactElement => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(MOBILE);
  // The layout chrome follows its own breakpoint (the top bar turns into a fixed overlay at 899px).
  const phoneChrome = useMediaQuery(FIXED_HEADER);
  const [limit, setLimit] = React.useState(pageSize);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  // The micro drag shows an order of its own while it plays, and the game in its hand; the list's order is never
  // touched.
  const [demoOrder, setDemoOrder] = React.useState<string[] | null>(null);
  const [demoLifted, setDemoLifted] = React.useState<string | null>(null);

  const allIds = React.useMemo(() => games.map(gameKey), [games]);
  const shown = React.useMemo(() => games.slice(0, limit), [games, limit]);
  const shownIds = React.useMemo(() => shown.map(gameKey), [shown]);
  const byId = React.useMemo(() => new Map(games.map((game) => [gameKey(game), game])), [games]);
  const tiles = React.useMemo(
    () =>
      demoOrder
        ? demoOrder.flatMap((id): SiteGame[] => {
            const game = byId.get(id);
            return game ? [game] : [];
          })
        : shown,
    [demoOrder, byId, shown],
  );
  const remaining = games.length - shown.length;

  const reorderShown = React.useCallback((head: string[]) => onReorder(replaceHead(allIds, head)), [allIds, onReorder]);
  const moveGame = React.useCallback(
    (id: string, to: number) => onReorder(moveId(allIds, id, to)),
    [allIds, onReorder],
  );
  // The band the list is visible in, for the auto-scroll while a tile is held (declared ahead of `band`
  // below, which it reads).
  const bandRef = React.useRef<() => ScreenInsets>(() => ({ top: 0, bottom: 0 }));
  const autoScrollBand = React.useCallback(() => bandRef.current(), []);
  // `longPress`: a touch screen wide enough for the grid (a tablet, 821 to 899px) lifts a tile by a resting finger,
  // or at once by its drag handle (`grip`); a finger never sends the drag events a mouse drag runs on.
  const sortable = useSortableGrid(demoOrder ?? shownIds, reorderShown, {
    autoScroll: autoScrollBand,
    longPress: true,
    grip: TILE_HANDLE,
  });
  const dragging = sortable.draggedId !== null;
  // Reports the drag to the page (which freezes its change bar for the duration). The cleanup reports
  // not-dragging so a grid unmounted mid-drag (the open list refetching, the route closing) cannot
  // leave that freeze on forever. The braces matter: the effect must not hand React the callback's value.
  React.useEffect(() => {
    onDraggingChange?.(dragging);
    return () => onDraggingChange?.(false);
  }, [dragging, onDraggingChange]);

  const reveal = React.useCallback(
    (index: number): void => {
      if (index >= 0) setLimit((current) => (index < current ? current : Math.ceil((index + 1) / pageSize) * pageSize));
    },
    [pageSize],
  );
  const filter = useGameListFilter({ games, providerNames, reveal, containerRef: wrapRef });
  const filtering = filter.matches !== null;
  // The filter's row covers the top of the list while it floats (filtering) or is pinned (with actions); drawn elsewhere
  // (`filterPortal`), it covers nothing.
  const rowPinned = !filterPortal && (filtering || Boolean(filterActions));
  // Measured when used (a menu opening, a move), so it always matches the layout of that moment.
  const band = React.useCallback(
    () => visibleBand(wrapRef.current, placement, phoneChrome, rowPinned),
    [placement, phoneChrome, rowPinned],
  );
  bandRef.current = band;

  /**
   * A move from a tile's menu. The page stays put: the tiles glide from where they are now, and when the
   * tile's new place is off screen (or not rendered) a copy of it drifts off in that direction instead.
   */
  const moveFromMenu = (id: string, to: number): void => {
    const slots = Array.from(wrapRef.current?.querySelectorAll<HTMLElement>('[data-id]') ?? []);
    const from = allIds.indexOf(id);
    const source = slots.find((el) => el.dataset.id === id);
    const destination = slots[to];
    if (source && to !== from && (!destination || !isOnScreen(destination, band()))) {
      sendAway(source, to < from ? 'up' : 'down');
      sortable.captureLayout(id);
    } else {
      sortable.captureLayout();
    }
    moveGame(id, to);
  };

  // The tiles are memoised, so what they are handed must keep its identity: the move reads the latest list
  // through a ref.
  const moveFromMenuRef = React.useRef(moveFromMenu);
  moveFromMenuRef.current = moveFromMenu;
  const onTileMove = React.useCallback((id: string, to: number) => moveFromMenuRef.current(id, to), []);

  const dragged = sortable.draggedId ? byId.get(sortable.draggedId) : undefined;

  return (
    <Wrap ref={wrapRef} $placement={placement}>
      {filterPortal ? (
        createPortal(
          <GameListFilter
            value={filter.query}
            onChange={filter.onQueryChange}
            current={filter.current}
            total={filter.total}
            onPrevious={filter.previous}
            onNext={filter.next}
            placement={placement}
            inline
          />,
          filterPortal,
        )
      ) : (
        <GameListFilter
          value={filter.query}
          onChange={filter.onQueryChange}
          current={filter.current}
          total={filter.total}
          onPrevious={filter.previous}
          onNext={filter.next}
          placement={placement}
          actions={filterActions}
          collapsible={collapsibleFilter}
        />
      )}

      {isMobile ? (
        <GameListRows
          games={shown}
          total={games.length}
          providerNames={providerNames}
          onMove={moveGame}
          matches={filter.matches}
          currentMatch={filter.currentMatch}
          onMenuOpen={filter.clearCurrent}
          band={band}
          placement={placement}
          unavailable={unavailable}
          onRemove={onRemove}
          artById={artById}
          demoLoop={demoLoop}
        />
      ) : (
        <Grid
          role="list"
          aria-label={t('Games in this list')}
          data-ftux={listTarget ? GAMES_LIST_TARGET : undefined}
          onDragOver={sortable.onGridDragOver}
          onDrop={(event) => event.preventDefault()}
        >
          {tiles.map((game, index) => {
            const id = gameKey(game);
            return (
              <GameTile
                key={id}
                game={game}
                index={index}
                total={games.length}
                providerNames={providerNames}
                placement={placement}
                dragging={sortable.draggedId === id || demoLifted === id}
                dimmed={filter.matches !== null && !filter.matches.has(id)}
                current={filter.matches !== null && filter.currentMatch === id}
                registerSlot={sortable.registerSlot}
                slotDragProps={sortable.slotDragProps}
                band={band}
                onMove={onTileMove}
                onMenuOpen={filter.clearCurrent}
                unavailable={unavailable.has(id)}
                // Only where it is offered: it changes with every reorder, and every other tile's props stay put.
                onRemove={unavailable.has(id) ? onRemove : undefined}
                artById={artById}
              />
            );
          })}
        </Grid>
      )}

      {remaining > 0 ? (
        <Footer>
          <MoreBtn type="button" onClick={() => setLimit((current) => current + pageSize)}>
            <Icon name="chevron-down" size={14} />
            <Text
              tag="span"
              text="Show {{count}} more of {{left}}"
              placeholders={{ count: Math.min(pageSize, remaining), left: remaining }}
            />
          </MoreBtn>
        </Footer>
      ) : null}

      {/* The tile being dragged, following the pointer (the native drag ghost is suppressed). */}
      {dragged && !isMobile && typeof document !== 'undefined'
        ? createPortal(
            <DragLayer ref={sortable.dragLayerRef} aria-hidden="true" style={{ transform: sortable.ghostTransform }}>
              <DragGhost style={{ width: `${sortable.ghostWidth}px` }}>
                <GameArt game={dragged} unavailable={unavailable.has(gameKey(dragged))} artById={artById} />
              </DragGhost>
            </DragLayer>,
            document.body,
          )
        : null}
      {/* The FTUX's micro drag on the tiles (phones: the rows play their own). Never during the owner's own drag: a
          native drag leaves the grid (pointercancel), which would let the demo play mid-drag and hand its order to the
          drop. */}
      {isMobile ? null : (
        <DragDemo
          loop={demoLoop && !dragging}
          ids={shownIds}
          wrapRef={wrapRef}
          captureLayout={sortable.captureLayout}
          onOrder={setDemoOrder}
          onLift={setDemoLifted}
          renderGhost={(id) => {
            const game = byId.get(id);
            return game ? <GameArt game={game} unavailable={unavailable.has(id)} artById={artById} /> : null;
          }}
          grip={TILE_GRIP}
        />
      )}
    </Wrap>
  );
};

export default GameListGrid;
